import { useEffect, useState, useRef } from "react";
import { Card, Col, Dropdown, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { deleteCatalogue, getCatalogues, upgradeManagedCatalogues, addAGGridSelectedRows} from "redux/slices/Catalogues";
import { Modal, Title } from "components";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "redux/store";
import { updateCatalogueField } from "redux/slices/Catalogues";
import { ManagedCatalogueSetting, SetStoreGroupSetting, updateStoreGroupField } from "redux/slices/Global";
import { ZIcon } from "components/ZIcon";
import { ZIconType } from "helpers/utility/iconMap";
import { ZTooltip } from "components/ZTooltip";
import { AgGridReact } from "ag-grid-react";
import { getCatalogueSummaryConfig, getCatalogueSummaryCoverPage, getCatalogueSummaryCsvDataFormatter, getCatalogueSummaryDataFormatter } from "TableConfigs/CatalogueSummaryConfig";
import { AgGridToolbar } from "components/AgGrid/AgGridToolbar";
import { Z } from "models/common/components";
import BaseCellRenderer from "components/AgGrid/CellRenderers/BaseCellRenderer";
import { getRowIds } from "helpers/data/dataHandling";

export function Catalogues() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const catalogues = useAppSelector((state: RootState) => state.catalogues);
	const user = useAppSelector((state: RootState) => state.logedinUser);
	const [showMigrationPrompt, setShowMigrationPrompt] = useState<boolean>(false);
	const global = useAppSelector((state: RootState) => state.global);
	const catalogueSummartyGridRef = useRef<AgGridReact>(null);

	const [showMigrationOption, setShowMigrationOption] = useState<boolean>(false);

	useEffect(() => {
		loadData();
	}, []);

	function loadData(){
		dispatch(getCatalogues()).then((res) => {
			const now = new Date().getTime();
			if(res.data.length == 0){
				catalogueSummartyGridRef.current?.api.showNoRowsOverlay()
			}
			//For the migration icon to be displayed on the main page
			const option = res.data.find(i => i.isOld) != undefined;
			setShowMigrationOption(option);

			if ((!global.managedCatalogue.nextShow || global.managedCatalogue.nextShow < now) && res.data.find(i => i.isOld)) {
				setShowMigrationPrompt(true);
				handleChange("promptForMigration", false);
			}
		});
	}

	function handleDeleteCatalogue() {
		let stockIds: number[] = [];
		catalogues.selectedRows.forEach((row: any) => {
			stockIds.push(row.id);
		});
		dispatch(deleteCatalogue({ ids: stockIds })).then(() => {
			dispatch(getCatalogues());
		});
	}

	function handleChange(option: any, optionValue: any) {
		dispatch(updateCatalogueField({ key: option, value: optionValue }));
	}

	function migrateCatalogues() {
		setShowMigrationPrompt(false);
		dispatch(upgradeManagedCatalogues()).then(() => {
			dispatch(getCatalogues());
		});
	}

	function migrateCataloguesPromptClose() {
		setShowMigrationPrompt(false);
		const newValue: ManagedCatalogueSetting = { nextShow: new Date(new Date().getTime() + 24 * 60 * 60 * 1000).getTime() };
		dispatch(updateStoreGroupField({ key: "managedCatalogue", value: newValue }))
		dispatch(SetStoreGroupSetting({ key: "managedCatalogue_migration", jsonSettings: JSON.stringify(newValue) }));
	}

	return (
		<>
			<Title pageTitle="Catalogues" appendTitle={showMigrationOption ? (
				<span onClick={() => setShowMigrationPrompt(true)} style={{ cursor: "pointer" }}>
					<ZTooltip text="Migrate old Catalogues" placement="top" className={"d-inline"}>
						<ZIcon icon={ZIconType.Alert} variant="danger" />
					</ZTooltip>
				</span>
			) : undefined} />

			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<div className="mb-1">
								<AgGridToolbar
									search={true}
									className="pb-1"
									handleSubmit={(searchState, filterState) => {
										catalogueSummartyGridRef.current?.api.clearRangeSelection();
										dispatch(addAGGridSelectedRows([]));
										catalogueSummartyGridRef.current?.api.setGridOption('quickFilterText', searchState?.keyword);                                            
									}}
									searchProps={
										{totalRows: catalogues.table.length, placeholder: "by Name", hideContains: true}
									}
									children={(
										<div className="d-inline-flex gap-1">
											{ 
											(catalogues.selectedRows != undefined && catalogues.selectedRows.length != 0) &&									
												<Z.Button variant="light" className="h-100" disabled={true} buttonText={catalogues.selectedRows.length+ " Selected"}/>
											}
												<ZTooltip text="Add">
												<Z.Button icon={ZIconType.Add} buttonSize="md" onClick={() => user.permissions.promotions.canCreate ? navigate("/app/catalogue/new") : undefined }/>
												</ZTooltip>
											<ZTooltip text={
												catalogues.selectedRows.length == 0? `Please select a row to delete`:
												catalogues.selectedRows.length == catalogues.table.length ? `Delete all rows`:									  
												catalogues.selectedRows.length> 0 ? `Delete ${catalogues.selectedRows.length} rows` :                                                    
												"Please select a row to delete"}>
												<Z.Button icon={ZIconType.Delete} buttonSize="md"  onClick={() => (user.permissions.promotions.canDelete ? handleDeleteCatalogue() : undefined)} 
													disabled={!user.permissions.promotions.canDelete || catalogues.selectedRows.length < 1} />
											</ZTooltip>
											<ZTooltip text="Refresh">
												<Z.Button icon={ZIconType.Refresh} buttonSize="md" onClick={() => loadData()} disabled={!user.permissions.promotions.canView} />
											</ZTooltip>
											<Dropdown>
												<ZTooltip text="Export">
													<Dropdown.Toggle variant="light" id="dropdown-basic" className="btn-icon" style={{height: 40.4}} >
														<ZIcon icon={ZIconType.Download} />
													</Dropdown.Toggle>
												</ZTooltip>
												<Dropdown.Menu className="w-auto" >
													<Dropdown.Item className="d-inline-flex align-items-center gap-1"
														
														onClick={() => {												
															catalogueSummartyGridRef.current?.api.exportDataAsCsv(getCatalogueSummaryCsvDataFormatter()); }}>
															<ZIcon icon={ZIconType.File} /> CSV File</Dropdown.Item>
													<Dropdown.Item className="d-inline-flex align-items-center gap-1" onClick={() => { 
														let filters="";
														const spreadSheets = [];
														spreadSheets.push(catalogueSummartyGridRef.current?.api.getSheetDataForExcel(getCatalogueSummaryCoverPage(filters)));
														spreadSheets.push(catalogueSummartyGridRef.current!.api.getSheetDataForExcel(getCatalogueSummaryDataFormatter()));
														catalogueSummartyGridRef.current!.api.exportMultipleSheetsAsExcel({
															data: spreadSheets,
															fileName: "Catalogues_" + new Date().toLocaleDateString('en-AU', {
																day : 'numeric',
																month : 'short',
																year : 'numeric',
																hour : '2-digit',
																minute : '2-digit'
															}).split(' ').join('-').replace(',', '') +".xlsx"
														});
													}}><ZIcon icon={ZIconType.Excel} /> Excel File</Dropdown.Item>									
												</Dropdown.Menu>
											</Dropdown>
											</div>
									)}
								/>
								<AgGridReact
									ref={catalogueSummartyGridRef}
									columnDefs={getCatalogueSummaryConfig()}
									rowData={ catalogues.table }
									pagination={true}
									paginationPageSize={20}
									className="ag-theme-quartz ag-theme-custom w-100"
									containerStyle={{ height: "75dvh" }}
									rowSelection="multiple"
									suppressRowClickSelection={true}
									suppressCellFocus={true}
									suppressContextMenu
									onSelectionChanged={(e) => {
										let formatedData:{
											positon: number,
											id: number
										} [] = [];
										catalogueSummartyGridRef.current?.api.getSelectedRows().forEach((xe: any) => formatedData.push({ positon: 0, id: xe.id}));
										dispatch(addAGGridSelectedRows(formatedData));
									}}
									onRowSelected={() => {
										let formatedData:{
											positon: number,
											id: number
										} [] = [];
										catalogueSummartyGridRef.current?.api.getSelectedRows().forEach((xe: any) => formatedData.push({ positon: 0, id: xe.id}));
										dispatch(addAGGridSelectedRows(formatedData));
									}}
									onCellClicked={(cell: any)=>{
										if(cell.column.getColId() == "name"){
											navigate(`/app/${cell.data.isOld ? 'catalogueView' : 'catalogue'}/${cell.data.id}`)
										}                       
									}}
									excelStyles={[{
										id: 'header',
										font: {
											 bold: true
										}
									}]}
									components={{
										baseCellRenderer: BaseCellRenderer,
									}}
								/>
							</div>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			<Modal
				title="Migrate Catalogues"
				show={showMigrationPrompt}
				buttonType="primary"
				buttonText={"Continue"}
				buttonCloseText="Cancel"
				handleSubmit={() => migrateCatalogues()}
				handleClose={() => migrateCataloguesPromptClose()}
				description={
					<>
						<p>As part of our custom lines module, we are migrating away from system only catalogues.</p>
						<p>Would you like to migrate your catalogues to the new version?</p>
					</>
				}
			/>
		</>
	);
}